<script setup lang="ts">
import AttachmentImage from '@/components/AttachmentImage.vue'
import FinestButton from '@/components/FinestButton.vue'
import FinestCard from '@/components/FinestCard.vue'
import { estateObjectAddressLine } from '@/utils/estate-object'
import EstateObjectsController from '@controllers/Investors/EstateObjectsController'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData

const props = defineProps<{
  estateObject: EstateObjectData
  projectId: string
  completion: number
  missingFields: number
}>()

const { t } = useI18n()

const addressLine = computed(() => estateObjectAddressLine(props.estateObject))

const cityLine = computed(
  () => [props.estateObject.zip_code, props.estateObject.city].filter(Boolean).join(' ') || null,
)

const objectType = computed(() =>
  props.estateObject.estate_object_type ? t(`enums.estateObjectType.${props.estateObject.estate_object_type}`) : null,
)

const isComplete = computed(() => props.completion >= 100)

const objectHref = computed(() =>
  EstateObjectsController.show(props.estateObject.id ?? '', {
    query: { submission_project_id: props.projectId },
  }),
)
</script>

<template>
  <FinestCard :title="t('title')" icon="pi pi-home" flush>
    <div class="bg-surface-3 relative mb-4 flex h-40 items-end p-3">
      <AttachmentImage
        v-if="estateObject.primary_photo?.path"
        :src="estateObject.primary_photo.path"
        :alt="addressLine"
        class="absolute inset-0 size-full object-cover" />
      <span
        v-else
        class="absolute inset-0 [background-image:var(--gradient-brand-soft)] opacity-15"
        aria-hidden="true" />

      <span v-if="objectType" class="bg-ink/70 relative rounded-full px-2.5 py-1 text-[11px] font-semibold text-white">
        {{ objectType }}
      </span>
    </div>

    <div class="px-5 pb-5">
      <p class="text-ink font-bold">{{ addressLine }}</p>
      <p v-if="cityLine" class="text-ink-2 text-sm">{{ cityLine }}</p>

      <div class="mt-4 flex items-baseline justify-between gap-3">
        <p class="font-bold tabular-nums">{{ t('completion', { percent: completion }) }}</p>
        <span
          class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
          :class="isComplete ? 'bg-success-subtle text-success-strong' : 'bg-warning-subtle text-warning'">
          {{ isComplete ? t('badge.complete') : t('badge.missing', { count: missingFields }, missingFields) }}
        </span>
      </div>

      <div class="bg-surface-3 mt-2 h-2 overflow-hidden rounded-full">
        <span
          class="block h-full [background-image:var(--gradient-brand)]"
          :style="{ width: `${completion}%` }"
          aria-hidden="true" />
      </div>

      <p class="text-ink-2 mt-3 text-sm">{{ t('hint') }}</p>

      <Link :href="objectHref" class="mt-4 block">
        <FinestButton class="w-full" :label="t('action')" icon="pi pi-arrow-right" icon-pos="right" />
      </Link>
    </div>
  </FinestCard>
</template>

<i18n lang="json">
{
  "de": {
    "title": "Investitionsobjekt",
    "action": "Zum Objekt",
    "completion": "Zu {percent} % vollständig",
    "hint": "Du kannst jederzeit weitere Angaben zum Objekt ergänzen — je vollständiger die Daten, desto schneller bekommst du deinen Kredit.",
    "badge": {
      "complete": "Bankreif",
      "missing": "{count} Angaben offen | {count} Angabe offen | {count} Angaben offen"
    }
  },
  "en": {
    "title": "Financed property",
    "action": "To the property",
    "completion": "{percent} % complete",
    "hint": "You can add further details about the property at any time — the more complete the data, the faster you get your loan.",
    "badge": {
      "complete": "Bank-ready",
      "missing": "{count} details open | {count} detail open | {count} details open"
    }
  }
}
</i18n>
